import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ArrowRight, BarChart3, Gamepad2, Layers, Zap } from "lucide-react";
import { Link } from "wouter";

export default function Home({ cards }) {
  return (
    <div className="flex flex-col gap-16 pb-16">
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background with Gradient */}
        <div className="absolute inset-0 z-0">
          <img
            src="/img/artem-shashkin-silco-zaun-lol.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-background/80 via-transparent to-background/80" />
        </div>

        {/* Content */}
        <div className="container relative z-10 flex flex-col items-center text-center gap-6 max-w-4xl px-4 animate-in fade-in zoom-in duration-700">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium mb-4 backdrop-blur-sm">
            <Zap className="w-4 h-4" />
            <span>Season 3 is now live!</span>
          </div>

          <h1 className="text-5xl md:text-7xl font-display font-bold tracking-tight text-white drop-shadow-2xl">
            MASTER THE <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">ARENA</span>
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl leading-relaxed">
            The ultimate companion for Riftbound. Build unbeatable decks, analyze meta statistics, and challenge friends in our custom lobby system.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mt-8 w-full sm:w-auto">
            <Link to="/play">
              <Button size="lg" className="w-full text-lg px-8 h-14 rounded-2xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(57,255,20,0.4)] transition-all hover:scale-105">
                <Gamepad2 className="mr-2 w-5 h-5" />
                Play Now
              </Button>
            </Link>
            <Link to="/decks">
              <Button variant="outline" size="lg" className="w-full text-lg px-8 h-14 rounded-2xl border-primary/30 hover:bg-primary/10 hover:border-primary/60 text-foreground backdrop-blur-sm transition-all hover:scale-105">
                <Layers className="mr-2 w-5 h-5" />
                Browse Decks
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link to="/play">
            <Card className="h-full group hover:border-primary/50 transition-all hover:shadow-[0_0_30px_rgba(57,255,20,0.1)] cursor-pointer bg-card/50 backdrop-blur-sm border-border/50 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-primary to-transparent" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl font-display">
                  <Gamepad2 className="w-8 h-8 text-primary group-hover:animate-pulse" />
                  Lobby System
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Create private lobbies, invite friends with unique codes, and organize tournaments effortlessly.
                </CardDescription>
                <div className="mt-4 flex items-center text-primary text-sm font-medium group-hover:translate-x-2 transition-transform">
                  Enter Arena <ArrowRight className="ml-1 w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/cards">
            <Card className="h-full group hover:border-secondary/50 transition-all hover:shadow-[0_0_30px_rgba(212,175,55,0.1)] cursor-pointer bg-card/50 backdrop-blur-sm border-border/50 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-secondary to-transparent" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl font-display">
                  <Layers className="w-8 h-8 text-secondary group-hover:animate-pulse" />
                  Card Database
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Explore the complete collection. Filter by region, rarity, and cost to find the perfect additions.
                </CardDescription>
                <div className="mt-4 flex items-center text-secondary text-sm font-medium group-hover:translate-x-2 transition-transform">
                  Browse Cards <ArrowRight className="ml-1 w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          </Link>

          <Link to="/stats">
            <Card className="h-full group hover:border-blue-500/50 transition-all hover:shadow-[0_0_30px_rgba(59,130,246,0.1)] cursor-pointer bg-card/50 backdrop-blur-sm border-border/50 overflow-hidden">
              <div className="h-2 bg-gradient-to-r from-blue-500 to-transparent" />
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl font-display">
                  <BarChart3 className="w-8 h-8 text-blue-500 group-hover:animate-pulse" />
                  Live Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base">
                  Track your win rates, analyze meta trends, and improve your gameplay with detailed analytics.
                </CardDescription>
                <div className="mt-4 flex items-center text-blue-500 text-sm font-medium group-hover:translate-x-2 transition-transform">
                  View Analytics <ArrowRight className="ml-1 w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Featured Decks Preview */}
      <section className="container">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-display font-bold">Trending Decks</h2>
          <Link to="/decks">
            <Button variant="ghost" className="text-muted-foreground hover:text-primary">
              View All <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-card/40 border-border/40 hover:border-primary/30 transition-all hover:-translate-y-1 group">
              <div className="aspect-video w-full bg-gradient-to-br from-[#1a2836] to-[#0f1923] relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="font-bold text-lg truncate">Hextech Control v{i}.0</h3>
                  <p className="text-xs text-muted-foreground">by ProPlayer{i}</p>
                </div>
              </div>
              <CardContent className="p-4 pt-3">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span className="flex items-center gap-1 text-primary"><Zap className="w-3 h-3" /> 58% WR</span>
                  <span>1.2k Views</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}